import { Fragment } from 'react';

interface TextRange {
  start: number;
  end: number;
}

function normalizeCharacter(character: string) {
  return character
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('sl-SI');
}

function findMatchingRanges(name: string, query: string): TextRange[] {
  const normalizedCharacters: string[] = [];
  const originalStarts: number[] = [];
  const originalEnds: number[] = [];
  let originalOffset = 0;

  for (const character of name) {
    const normalized = normalizeCharacter(character);
    for (const normalizedCharacter of normalized) {
      normalizedCharacters.push(normalizedCharacter);
      originalStarts.push(originalOffset);
      originalEnds.push(originalOffset + character.length);
    }
    originalOffset += character.length;
  }

  const normalizedName = normalizedCharacters.join('');
  const tokens = [
    ...new Set(normalizeCharacter(query).match(/[\p{L}\p{N}]+/gu) ?? []),
  ];
  const ranges: TextRange[] = [];

  for (const token of tokens) {
    let searchFrom = 0;
    while (searchFrom < normalizedName.length) {
      const matchStart = normalizedName.indexOf(token, searchFrom);
      if (matchStart === -1) break;
      const matchEnd = matchStart + token.length;
      ranges.push({
        start: originalStarts[matchStart] ?? matchStart,
        end: originalEnds[matchEnd - 1] ?? matchEnd,
      });
      searchFrom = matchEnd;
    }
  }

  return ranges
    .sort((left, right) => left.start - right.start || left.end - right.end)
    .reduce<TextRange[]>((merged, range) => {
      const previous = merged.at(-1);
      if (previous && range.start <= previous.end) {
        previous.end = Math.max(previous.end, range.end);
      } else {
        merged.push({ ...range });
      }
      return merged;
    }, []);
}

export function HighlightedProductName({
  name,
  query,
}: {
  name: string;
  query: string;
}) {
  const ranges = findMatchingRanges(name, query);
  if (ranges.length === 0) return name;

  return (
    <>
      {ranges.map((range, index) => {
        const previousEnd = index === 0 ? 0 : (ranges[index - 1]?.end ?? 0);
        const plainText = name.slice(previousEnd, range.start);
        const matchedText = name.slice(range.start, range.end);
        return (
          <Fragment key={`${range.start}-${range.end}`}>
            {plainText}
            <strong className="product-name-match">{matchedText}</strong>
          </Fragment>
        );
      })}
      {name.slice(ranges.at(-1)?.end ?? 0)}
    </>
  );
}
